import { useState, useEffect, useRef, useMemo, Dispatch, SetStateAction } from 'react'
import { useShowContent } from 'hooks/shows-master'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

interface ShowCardDetails {
    showId: string
    title: string
    shortSummary: string
    episodeCount: number
    selectedEpisodes: string[]
}

interface CardDetailsProps {
    showIds: string[]
    cardDetails: Record<string, ShowCardDetails>
    onCardDetailsChange: Dispatch<SetStateAction<Record<string, ShowCardDetails>>>
    onSave?: () => void
}

const ShowContentLoader = ({ showId, onDataLoad }: { showId: string; onDataLoad: (data: any) => void }) => {
    const query = useShowContent(showId, { enabled: !!showId })
    const onDataLoadRef = useRef(onDataLoad)

    useEffect(() => {
        onDataLoadRef.current = onDataLoad
    }, [onDataLoad])

    useEffect(() => {
        if (query.data) {
            onDataLoadRef.current(query.data)
        }
    }, [query.data])

    return null
}

const CardDetails = ({ showIds, cardDetails, onCardDetailsChange, onSave }: CardDetailsProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedEpisodes, setSelectedEpisodes] = useState<Record<string, string[]>>({})
    const [showsData, setShowsData] = useState<Record<string, any>>({})
    const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({})

    const dataLoadHandlers = useMemo(() => {
        const handlers: Record<string, (data: any) => void> = {}
        showIds.forEach(showId => {
            handlers[showId] = (data: any) => {
                setShowsData(prev => ({ ...prev, [showId]: data }))
            }
        })
        return handlers
    }, [showIds])

    // Track initialized showIds to prevent re-initialization
    const initializedShowIdsRef = useRef<Set<string>>(new Set())
    const lastCardDetailsRef = useRef<Record<string, any>>({})
    const lastSentCardDetailsRef = useRef<Record<string, ShowCardDetails>>({})

    // Initialize selectedEpisodes from cardDetails prop when available
    useEffect(() => {
        // Check if cardDetails actually changed
        const cardDetailsStr = JSON.stringify(cardDetails)
        const lastCardDetailsStr = JSON.stringify(lastCardDetailsRef.current)
        
        if (cardDetails && Object.keys(cardDetails).length > 0 && cardDetailsStr !== lastCardDetailsStr) {
            // Check if this update came from our own internal update (to prevent loop)
            const lastSentStr = JSON.stringify(lastSentCardDetailsRef.current)
            const matchesLastSent = Object.keys(cardDetails).every(showId => {
                const propDetail = cardDetails[showId]
                const sentDetail = lastSentCardDetailsRef.current[showId]
                if (!sentDetail) return false
                return propDetail.title === sentDetail.title &&
                    propDetail.shortSummary === sentDetail.shortSummary &&
                    propDetail.episodeCount === sentDetail.episodeCount &&
                    JSON.stringify(propDetail.selectedEpisodes) === JSON.stringify(sentDetail.selectedEpisodes)
            })
            
            // If this matches what we just sent, skip initialization to prevent loop
            if (matchesLastSent && lastSentStr !== '{}') {
                lastCardDetailsRef.current = cardDetails
                return
            }
            
            lastCardDetailsRef.current = cardDetails
            
            const initialSelectedEpisodes: Record<string, string[]> = {}
            let hasNewEpisodes = false
            
            showIds.forEach(showId => {
                const detail = cardDetails[showId]
                if (detail && detail.selectedEpisodes && Array.isArray(detail.selectedEpisodes)) {
                    // Only initialize if not already initialized for this showId
                    const wasInitialized = initializedShowIdsRef.current.has(showId)
                    
                    if (!wasInitialized) {
                        initialSelectedEpisodes[showId] = detail.selectedEpisodes
                        hasNewEpisodes = true
                        initializedShowIdsRef.current.add(showId)
                    }
                }
            })
            
            // Only update if we have new episodes to set
            if (hasNewEpisodes && Object.keys(initialSelectedEpisodes).length > 0) {
                setSelectedEpisodes(prev => {
                    const hasChanges = Object.keys(initialSelectedEpisodes).some(showId => {
                        const existing = prev[showId] || []
                        const incoming = initialSelectedEpisodes[showId] || []
                        return JSON.stringify(existing) !== JSON.stringify(incoming)
                    })
                    return hasChanges ? { ...prev, ...initialSelectedEpisodes } : prev
                })
            }
        }
        
        // Reset initialization tracking when showIds change significantly
        if (showIds.length === 0) {
            initializedShowIdsRef.current.clear()
            lastCardDetailsRef.current = {}
            lastSentCardDetailsRef.current = {}
        }
    }, [cardDetails, showIds])

    // Track last update to prevent unnecessary re-renders
    const lastUpdateRef = useRef<string>('')

    useEffect(() => {
        const updatedDetails: Record<string, ShowCardDetails> = {}

        showIds.forEach(showId => {
            const data = showsData[showId]
            if (data?.data?.show) {
                const show = data.data.show
                const episodes = show.episodes || []
                const episodesList = selectedEpisodes[showId] || []

                updatedDetails[showId] = {
                    showId,
                    title: show.name || '',
                    shortSummary: show.short_summary || '',
                    episodeCount: episodes.length,
                    selectedEpisodes: episodesList
                }
            }
        })

        if (Object.keys(updatedDetails).length > 0) {
            // Create a signature of the update to prevent duplicate calls
            const updateSignature = JSON.stringify({
                showIds: showIds.sort(),
                episodes: Object.keys(selectedEpisodes).sort().map(id => ({
                    id,
                    episodes: selectedEpisodes[id]
                })),
                shows: Object.keys(updatedDetails).sort().map(id => ({
                    id,
                    title: updatedDetails[id].title,
                    summary: updatedDetails[id].shortSummary,
                    count: updatedDetails[id].episodeCount
                }))
            })

            // Only update if signature changed
            if (lastUpdateRef.current !== updateSignature) {
                lastUpdateRef.current = updateSignature
                
                onCardDetailsChange((prev: Record<string, ShowCardDetails>) => {
                    const hasChanges = Object.keys(updatedDetails).some(showId => {
                        const existing = prev[showId]
                        const updated = updatedDetails[showId]
                        if (!existing) return true
                        return existing.title !== updated.title ||
                            existing.shortSummary !== updated.shortSummary ||
                            existing.episodeCount !== updated.episodeCount ||
                            JSON.stringify(existing.selectedEpisodes) !== JSON.stringify(updated.selectedEpisodes)
                    })

                    if (hasChanges) {
                        // Track what we're sending to prevent loop
                        lastSentCardDetailsRef.current = { ...updatedDetails }
                        return { ...prev, ...updatedDetails }
                    }
                    return prev
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEpisodes, showsData, showIds])

    const handleEpisodeToggle = (showId: string, episodeId: string) => {
        setSelectedEpisodes(prev => {
            const current = prev[showId] || []
            const index = current.indexOf(episodeId)

            if (index > -1) {
                return {
                    ...prev,
                    [showId]: current.filter(id => id !== episodeId)
                }
            } else {
                if (current.length >= 2) {
                    return prev
                }
                return {
                    ...prev,
                    [showId]: [...current, episodeId]
                }
            }
        })
    }

    const getShowEpisodes = (showId: string) => {
        const data = showsData[showId]
        if (!data?.data?.show) return []

        const episodes = data.data.show.episodes || []
        return episodes.map((ep: any) => ({
            value: ep.episode?.id || ep.id || '',
            label: ep.episode?.name || ep.name || ''
        }))
    }

    const getEpisodeLabel = (showId: string, episodeId: string) => {
        const episodes = getShowEpisodes(showId)
        return episodes.find(ep => ep.value === episodeId)?.label || episodeId
    }

    if (showIds.length === 0) return null

    return (
        <div className="space-y-4">
            {showIds.map(showId => (
                <ShowContentLoader key={showId} showId={showId} onDataLoad={dataLoadHandlers[showId]} />
            ))}

            <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full flex items-center justify-between p-4 text-left"
                >
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Card Details Configuration
                    </h3>
                    {isOpen ? (
                        <ChevronUpIcon className="h-5 w-5 text-slate-500" />
                    ) : (
                        <ChevronDownIcon className="h-5 w-5 text-slate-500" />
                    )}
                </button>

                {isOpen && (
                    <div className="px-4 pb-4 space-y-4">
                        <div className="text-xs text-slate-500 mb-4">
                            Select exactly 2 episodes for each show. First selected = top thumbnail, second = bottom thumbnail.
                        </div>

                        <div className="space-y-4">
                            {showIds.map(showId => {
                                const data = showsData[showId]
                                const show = data?.data?.show
                                const episodes = getShowEpisodes(showId)
                                const currentSelected = selectedEpisodes[showId] || []
                                const isDropdownOpen = dropdownOpen[showId] || false

                                if (!show) return null

                                return (
                                    <div key={showId} className="border border-slate-200 rounded-lg p-4 dark:border-slate-600">
                                        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                                            {show.name || `Show ${showId}`}
                                        </h4>

                                        <div className="space-y-2 text-xs text-slate-600 dark:text-slate-400 mb-4">
                                            <p><span className="font-medium">Title:</span> {show.name || 'N/A'}</p>
                                            <p><span className="font-medium">Short Summary:</span> {show.short_summary || 'N/A'}</p>
                                            <p><span className="font-medium">Number of Episodes:</span> {(show.episodes || []).length}</p>
                                        </div>

                                        <div className="relative">
                                            <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                                                Select Episodes ({currentSelected.length}/2)
                                            </label>

                                            <button
                                                type="button"
                                                onClick={() => setDropdownOpen(prev => ({ ...prev, [showId]: !prev[showId] }))}
                                                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-left
                                                    text-slate-900 placeholder-slate-400
                                                    focus:outline-none focus:ring-2 focus:ring-blue-500
                                                    dark:border-slate-600 dark:bg-slate-800 dark:text-white flex items-center justify-between"
                                            >
                                                <span>
                                                    {currentSelected.length === 0
                                                        ? 'Select 2 episodes...'
                                                        : currentSelected.map((id, idx) => (
                                                            <span key={id} className="mr-2">
                                                                {idx + 1}. {getEpisodeLabel(showId, id)}
                                                            </span>
                                                        ))
                                                    }
                                                </span>
                                                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {isDropdownOpen && (
                                                <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg dark:bg-slate-800 dark:border-slate-600">
                                                    {episodes.map((episode) => {
                                                        const isSelected = currentSelected.includes(episode.value)
                                                        const isDisabled = !isSelected && currentSelected.length >= 2

                                                        return (
                                                            <button
                                                                key={episode.value}
                                                                type="button"
                                                                onClick={() => !isDisabled && handleEpisodeToggle(showId, episode.value)}
                                                                disabled={isDisabled}
                                                                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2
                                                                    ${isSelected
                                                                        ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                                                                        : isDisabled
                                                                            ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                                                            : 'hover:bg-blue-50 dark:hover:bg-slate-700'
                                                                    }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => { }}
                                                                    className="h-4 w-4 accent-blue-600"
                                                                    disabled={isDisabled}
                                                                />
                                                                <span>{episode.label}</span>
                                                                {isSelected && (
                                                                    <span className="ml-auto text-xs text-blue-600">
                                                                        #{currentSelected.indexOf(episode.value) + 1}
                                                                    </span>
                                                                )}
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            )}

                                            {currentSelected.length === 2 && (
                                                <div className="mt-2 text-xs text-green-600">
                                                    ✓ Selected: 1st = Top thumbnail, 2nd = Bottom thumbnail
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        <div className="pt-4 border-t border-slate-200 dark:border-slate-600">
                            <div className="text-xs text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-md">
                                ✓ Card details are automatically saved as you make changes
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CardDetails
